import React, { useEffect, useState } from 'react';
import Radio from '@mui/material/Radio';
import RadioGroupMui from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { InputLabel } from '@wajeez-web/components/ui';
import { useTranslation } from 'react-i18next';
import { ControllerFieldState } from 'react-hook-form';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';

type RadioOption = {
  value: string;
  label: string;
};

interface AutocompleteProps {
  options: RadioOption[];
  onChange: (value: string | undefined) => void;
  id?: string | undefined;
  value?: string;
  defaultValue?: string;
  label?: string;
  color?: string;
  required?: boolean;
  isError?: boolean;
  fieldState?: ControllerFieldState;
  clearable?: boolean;
}

export const RadioGroup = React.forwardRef<
  typeof RadioGroupMui,
  AutocompleteProps
>(
  (
    {
      options,
      onChange,
      id,
      value,
      defaultValue,
      label,
      required,
      isError,
      fieldState,
      clearable,
      color = 'var(--theme-primary)',
    },
    ref
  ) => {
    const [valueLocal, setValueLocal] = useState<string | undefined>(value);
    const [optionsLocal, setOptionsLocal] = useState<RadioOption[]>(options);

    const { t } = useTranslation();

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setValueLocal((event.target as HTMLInputElement).value);
    };

    const mapOptions = () => {
      return optionsLocal.map((o) => {
        return (
          <FormControlLabel
            key={o.value}
            value={o.value}
            control={
              <Radio
                sx={{
                  color: color,
                  '&.Mui-checked': {
                    color: color,
                  },
                }}
              />
            }
            label={o.label}
          />
        );
      });
    };

    useEffect(() => {
      setValueLocal(value);
    }, [value]);

    useEffect(() => {
      onChange(valueLocal);
    }, [valueLocal]);

    useEffect(() => {
      if (clearable) {
        setOptionsLocal([...options, { value: '', label: 'None' }]);
      } else {
        setOptionsLocal([...options]);
      }
    }, [options, clearable]);

    return (
      <div className="relative">
        <FormControl>
          {label && (
            <InputLabel
              name={id ?? ''}
              text={
                t(`inputs.labels.${label}`, label.toString() || '') as string
              }
              required={required}
            />
          )}
          <RadioGroupMui
            ref={ref}
            aria-labelledby={id}
            defaultValue={defaultValue}
            value={valueLocal}
            onChange={handleChange}
          >
            {mapOptions()}
          </RadioGroupMui>
        </FormControl>
        {isError && (
          <div className="absolute top-[105%] right-0 text-[12px] text-red-500">
            <ReturnTranslatedText
              origintext={fieldState?.error?.message}
              text={`general.${fieldState?.error?.message}`}
            />
          </div>
        )}
      </div>
    );
  }
);
